import { useEffect } from "react";
import { ActionIcon, Menu } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import IconCheckRounded from "@common/components/icons/IconCheckRounded/IconCheckRounded";
import IconDots from "@common/components/icons/IconDots/IconDots";
import IconWarning from "@common/components/icons/IconWarning";
import { darkNotificationProps } from "@common/constants/theme-mantine/styling";
import useChangePlanWithInstanceRestart from "@hooks/useChangePlanWithInstanceRestart";
import { showSuccessNotification } from "@utils/notifications";
import { useAddOnContext } from "../hooks";
import { IAddOnSlotProps, IMeteredAddOnDescriptor } from "../types";

export function MoreMenu({
  addOn: advanced,
}: IAddOnSlotProps<IMeteredAddOnDescriptor>) {
  const basic = useAddOnContext("transforms-basic-metered");
  const { purchase, subscribed, isCloudHosted } = advanced;

  const {
    isPending: loadingChangePlan,
    isUpdatingSubscription,
    error: changePlanError,
    mutate: changePlan,
  } = useChangePlanWithInstanceRestart(purchase);

  const onSuccess = () =>
    showSuccessNotification({
      ...darkNotificationProps,
      icon: <IconCheckRounded />,
      message: "Transforms successfully updated",
    });

  const downgrade = () =>
    changePlan(basic.getEnableAddOnPayload(), { onSuccess });

  // self-hosted has no basic metered row to fall back to
  const disableTransforms = () =>
    changePlan(advanced.getDisableAddOnPayload(), { onSuccess });

  useEffect(() => {
    if (!changePlanError) {
      return;
    }
    showNotification({
      ...darkNotificationProps,
      icon: <IconWarning />,
      autoClose: 5000,
      message: changePlanError.message || changePlanError.status,
    });
  }, [changePlanError]);

  if (!subscribed) {
    return null;
  }

  return (
    <Menu withinPortal position="bottom-end">
      <Menu.Target data-testid="TransformsMoreMenu_toggle">
        <ActionIcon
          aria-label="Common actions"
          ml="xs"
          loading={isUpdatingSubscription}
          disabled={loadingChangePlan}
        >
          <IconDots />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        {isCloudHosted ? (
          <Menu.Item
            data-testid="TransformsMoreMenu_downgrade"
            onClick={downgrade}
          >
            Downgrade to basic
          </Menu.Item>
        ) : (
          <Menu.Item
            data-testid="TransformsMoreMenu_disable"
            onClick={disableTransforms}
          >
            Disable transforms
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
